import React from "react";
import { Users } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatCurrency, formatPercent } from "./dashboardUtils";
import type { Deal, Sale } from "../types";

interface TeamPerformanceCardProps {
  deals: Deal[];
  salesTeam: Sale[];
  wonStages: string[];
  lostStages: string[];
  currency?: string;
}

export const TeamPerformanceCard: React.FC<TeamPerformanceCardProps> = ({
  deals,
  salesTeam,
  wonStages,
  lostStages,
  currency = "BRL",
}) => {
  const memberStats = React.useMemo(() => {
    const statsMap = new Map<
      string,
      {
        id: string;
        name: string;
        initials: string;
        totalDeals: number;
        wonDeals: number;
        openDeals: number;
        wonAmount: number;
        totalAmount: number;
      }
    >();

    // Inicializa membros da equipe
    salesTeam.forEach((s) => {
      const id = String(s.id);
      const name =
        `${s.first_name || ""} ${s.last_name || ""}`.trim() || "Vendedor";
      const initials =
        (s.first_name?.[0] || "") + (s.last_name?.[0] || "") || "VD";
      statsMap.set(id, {
        id,
        name,
        initials: initials.toUpperCase(),
        totalDeals: 0,
        wonDeals: 0,
        openDeals: 0,
        wonAmount: 0,
        totalAmount: 0,
      });
    });

    // Sem responsável atribuído
    statsMap.set("unassigned", {
      id: "unassigned",
      name: "Não Atribuído",
      initials: "NA",
      totalDeals: 0,
      wonDeals: 0,
      openDeals: 0,
      wonAmount: 0,
      totalAmount: 0,
    });

    deals.forEach((d) => {
      const salesId = d.sales_id ? String(d.sales_id) : "unassigned";
      let stat = statsMap.get(salesId);
      if (!stat) {
        stat = {
          id: salesId,
          name: `Vendedor #${salesId}`,
          initials: "VD",
          totalDeals: 0,
          wonDeals: 0,
          openDeals: 0,
          wonAmount: 0,
          totalAmount: 0,
        };
        statsMap.set(salesId, stat);
      }

      stat.totalDeals += 1;
      const amount = Number(d.amount) || 0;
      stat.totalAmount += amount;

      const isWon =
        d.status === "ganho" ||
        wonStages.some(
          (s) => s.toLowerCase() === (d.stage || "").toLowerCase(),
        );
      const isLost =
        d.status === "perdido" ||
        lostStages.some(
          (s) => s.toLowerCase() === (d.stage || "").toLowerCase(),
        );

      if (isWon) {
        stat.wonDeals += 1;
        stat.wonAmount += amount;
      } else if (!isLost) {
        stat.openDeals += 1;
      }
    });

    // Filtra membros não atribuídos se não tiverem deals, e ordena por total de vendas/leads
    return Array.from(statsMap.values())
      .filter((s) => s.id !== "unassigned" || s.totalDeals > 0)
      .sort((a, b) => b.wonAmount - a.wonAmount || b.totalDeals - a.totalDeals);
  }, [deals, salesTeam, wonStages, lostStages]);

  const maxWonAmount = Math.max(...memberStats.map((s) => s.wonAmount), 1);

  return (
    <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
      <div className="flex items-center justify-between border-b border-border/50 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
            <Users className="w-3.5 h-3.5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Desempenho da Equipe
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Quantidade de leads, fechamentos e receita gerada por vendedor
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
          {memberStats.length} vendedores
        </span>
      </div>

      <div className="mt-4 overflow-x-auto">
        {memberStats.length === 0 ? (
          <div className="text-center py-8 text-xs text-muted-foreground">
            Nenhum membro da equipe com atividades registradas
          </div>
        ) : (
          <div className="space-y-3.5">
            {memberStats.map((member, idx) => {
              const convRate =
                member.totalDeals > 0
                  ? (member.wonDeals / member.totalDeals) * 100
                  : 0;
              const barWidth = Math.min(
                (member.wonAmount / maxWonAmount) * 100,
                100,
              );

              return (
                <div
                  key={member.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 p-2.5 rounded-lg border border-border/40 hover:border-border/80 bg-muted/20 transition-all"
                >
                  {/* Identificação do Vendedor */}
                  <div className="flex items-center gap-2.5 min-w-[180px]">
                    <div className="relative">
                      <Avatar className="w-8 h-8 text-xs font-semibold border">
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {member.initials}
                        </AvatarFallback>
                      </Avatar>
                      {idx === 0 && member.wonAmount > 0 && (
                        <div
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[9px] shadow-xs"
                          title="Líder de Vendas"
                        >
                          👑
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-foreground truncate block max-w-[150px]">
                        {member.name}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {member.totalDeals}{" "}
                        {member.totalDeals === 1
                          ? "lead total"
                          : "leads totais"}
                      </span>
                    </div>
                  </div>

                  {/* Métricas do Vendedor */}
                  <div className="flex items-center gap-4 text-xs">
                    <div className="text-right">
                      <span className="text-muted-foreground text-[10px] block">
                        Em Aberto
                      </span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">
                        {member.openDeals}
                      </span>
                    </div>

                    <div className="text-right">
                      <span className="text-muted-foreground text-[10px] block">
                        Ganhos
                      </span>
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {member.wonDeals} ({formatPercent(convRate)})
                      </span>
                    </div>

                    <div className="text-right min-w-[100px]">
                      <span className="text-muted-foreground text-[10px] block">
                        Faturado
                      </span>
                      <span className="font-bold text-foreground">
                        {formatCurrency(member.wonAmount, currency)}
                      </span>
                    </div>
                  </div>
                  {/* Barra de participação nas vendas */}
                  <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
